// Centralized accessibility configuration

export interface AccessibilitySettings {
  // Visual
  highContrast: boolean;
  largeText: boolean;
  fontSize: number; // 14-24
  reducedMotion: boolean;
  colorBlindMode: 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia';

  // Dyslexia
  dyslexiaFont: boolean;
  lineSpacing: number; // 1.5-3.0
  letterSpacing: number; // 0-0.2em
  syllableHighlight: boolean;

  // Motor
  largeClickTargets: boolean;
  keyboardOnly: boolean;
  stickyKeys: boolean;

  // Audio
  screenReaderOptimized: boolean;
  textToSpeech: boolean;
  ttsRate: number; // 0.5-2.0
  ttsVoice: string;

  // Voice
  voiceInput: boolean;
}

export const DEFAULT_ACCESSIBILITY_SETTINGS: AccessibilitySettings = {
  // Visual
  highContrast: false,
  largeText: false,
  fontSize: 16,
  reducedMotion: false,
  colorBlindMode: 'none',

  // Dyslexia
  dyslexiaFont: false,
  lineSpacing: 1.5,
  letterSpacing: 0,
  syllableHighlight: false,

  // Motor
  largeClickTargets: false,
  keyboardOnly: false,
  stickyKeys: false,

  // Audio
  screenReaderOptimized: false,
  textToSpeech: false,
  ttsRate: 1.0,
  ttsVoice: '',

  // Voice
  voiceInput: false,
};

// #53 fix (2026-05-17): explicit `category` field on each shortcut so the
// overlay no longer categorizes by string-prefix hacks like `key.startsWith('Alt')`.
// The previous prefix scheme miscategorized Alt+A and Alt+S into Navigation
// despite being Accessibility shortcuts, requiring opaque "key !== 'Alt+A'"
// guards. Categories are now data, not parsing.
export type ShortcutCategory = 'Navigation' | 'Editor' | 'Accessibility';

export interface ShortcutDef {
  category: ShortcutCategory;
  description: string;
  /**
   * What pressing the chord does. `GlobalShortcuts` dispatches on this, and its
   * test asserts every entry here has a branch — so a chord cannot be advertised
   * without an action again.
   */
  action: { type: 'navigate'; to: string } | { type: 'focus'; targetId: string };
}

/**
 * The chords that work ANYWHERE in the app, bound by
 * `src/components/Accessibility/GlobalShortcuts.tsx`.
 *
 * S132: this used to be `KEYBOARD_SHORTCUTS_BY_CATEGORY`, twelve chords of which
 * TWO reached an action. It advertised Alt+1–Alt+5 as navigation while the
 * canonical catalogue in `keyboardShortcutService` bound Alt+1–Alt+3 to the
 * editor's HTML/CSS/JS tabs, so the two catalogues disagreed about the same
 * keystroke; it advertised Redo as Ctrl+Y where the implementation is
 * Ctrl+Shift+Z; six of its chords appeared in no catalogue at all; and the only
 * component that displayed any of it was never mounted, so the dialog it
 * describes could not be opened. A child read the list and pressed four keys
 * that did nothing.
 *
 * The numbers now belong to the editor, where the canonical catalogue already
 * put them, and global navigation uses mnemonic letters. Every entry below is
 * dispatched by GlobalShortcuts, and the Editor rows in the overlay come from
 * `SHORTCUTS` filtered to the ones something actually registers — so neither
 * half of the dialog can advertise a key that does nothing.
 */
export const GLOBAL_SHORTCUTS: Record<string, ShortcutDef> = {
  'Alt+D': {
    category: 'Navigation',
    description: 'Go to Dashboard',
    action: { type: 'navigate', to: '/dashboard' },
  },
  'Alt+N': {
    category: 'Navigation',
    description: 'Start a new game',
    action: { type: 'navigate', to: '/project/new' },
  },
  'Alt+L': {
    category: 'Navigation',
    description: 'Go to Lessons',
    action: { type: 'navigate', to: '/curriculum' },
  },
  'Alt+G': {
    category: 'Navigation',
    description: 'Go to the Gallery',
    action: { type: 'navigate', to: '/gallery' },
  },
  'Alt+H': {
    category: 'Navigation',
    description: 'Open Help',
    action: { type: 'navigate', to: '/help' },
  },

  'Alt+A': {
    category: 'Accessibility',
    description: 'Open Accessibility Settings',
    action: { type: 'navigate', to: '/accessibility/settings' },
  },
  'Alt+S': {
    category: 'Accessibility',
    description: 'Skip to Main Content',
    action: { type: 'focus', targetId: 'main-content' },
  },
};

/**
 * The chord that opens the shortcuts dialog. Kept out of `GLOBAL_SHORTCUTS`
 * because the overlay owns it — it toggles component state rather than
 * navigating — while still being listed in the dialog it opens.
 */
export const SHORTCUTS_OVERLAY_CHORD = '?';
export const SHORTCUTS_OVERLAY_DEF: ShortcutDef & { action: { type: 'focus'; targetId: string } } = {
  category: 'Accessibility',
  description: 'Show Keyboard Shortcuts',
  // Not dispatched by GlobalShortcuts; the overlay handles it. The action is
  // recorded so the type stays uniform for rendering.
  action: { type: 'focus', targetId: 'shortcuts-title' },
};

/**
 * S85: the one mapping for colour-blind mode, read by AccessibilityContext.
 *
 * This used to be `COLORBLIND_MODE_FILTERS`, holding `url(#protanopia-filter)`
 * strings that NOTHING imported — the context re-derived the identical strings in
 * a private switch, so two copies of one mapping existed and editing this file
 * changed nothing on screen. It now holds the CSS classes the stylesheet defines,
 * which is the mechanism that actually drives the feature, and the context imports
 * it. `accessibility.css` owns which filter each class applies.
 */
export const COLORBLIND_MODE_CLASSES = {
  none: null,
  protanopia: 'a11y-colorblind-protanopia',
  deuteranopia: 'a11y-colorblind-deuteranopia',
  tritanopia: 'a11y-colorblind-tritanopia',
} as const;

export type ColorBlindMode = keyof typeof COLORBLIND_MODE_CLASSES;

export const FONT_SIZE_CONFIG = {
  min: 14,
  max: 24,
  step: 1,
  default: 16,
};

/**
 * The floor "Large Text" raises body text to, matching accessibility.css.
 *
 * S114: this used to live only inside AccessibilityContext, so the panel's own
 * readout printed the raw slider value while the page was painted with the
 * floor applied — the number a child read was not the number in effect.
 */
export const LARGE_TEXT_MIN_PX = 18;

/**
 * The floors Dyslexia-Friendly Text raises spacing to.
 *
 * S134: the stylesheet asked for the spacing half of the mode as CSS variable
 * FALLBACKS — `letter-spacing: var(--a11y-letter-spacing, 0.05em)` and
 * `line-height: var(--a11y-line-spacing, 1.5)` — and a fallback applies only
 * when the variable is UNSET. `AccessibilityContext` sets both on <body>
 * unconditionally, on mount, from the defaults (0em and 1.5), so the 0.05em was
 * never reached and the 1.5 was what the body already had. Combined with the
 * unbundled typeface (S24, still open, needs a decision about a binary asset),
 * ticking "Dyslexia-Friendly Text" on a Chromebook changed NOTHING on screen.
 *
 * These are floors rather than fixed values, exactly as `LARGE_TEXT_MIN_PX` is:
 * a child who has already dragged the sliders higher keeps their setting.
 */
export const DYSLEXIA_MIN_LETTER_SPACING = 0.05;
export const DYSLEXIA_MIN_LINE_SPACING = 1.8;

export const LINE_SPACING_CONFIG = {
  min: 1.5,
  max: 3.0,
  step: 0.1,
  default: 1.5,
};

export const LETTER_SPACING_CONFIG = {
  min: 0,
  max: 0.2,
  step: 0.01,
  default: 0,
};

export const TTS_RATE_CONFIG = {
  min: 0.5,
  max: 2.0,
  step: 0.1,
  default: 1.0,
};

// S133: `SUPPORTED_TTS_VOICES` used to sit here — five hand-written display
// labels ('British English', 'American English (Male)') that nothing imported.
// Even wired into the panel as a dropdown, every choice would have missed:
// `useTextToSpeech` resolves a voice by
// `voices.find((v) => v.name === settings.ttsVoice)` against the browser's own
// SpeechSynthesisVoice names ('Google UK English Female', 'Samantha', …), which
// no platform reports under those labels — and the miss was silent, since the
// `if (selectedVoice)` has no else. Three of the five entries shared one
// language tag and differed only by a label no platform reports, which is the
// tell that the list was written by hand rather than read from
// `speechSynthesis.getVoices()`. The panel now offers exactly the voices the
// device has, and says so when a saved one is missing.

export const ACCESSIBILITY_CSS_CLASSES = {
  highContrast: 'a11y-high-contrast',
  largeText: 'a11y-large-text',
  dyslexiaFont: 'a11y-dyslexia-font',
  reducedMotion: 'a11y-reduced-motion',
  largeTargets: 'a11y-large-targets',
  keyboardFocus: 'a11y-keyboard-focus',
  screenReaderOptimized: 'a11y-screen-reader',
};
